/* eslint-disable */
import { useParams, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../store/hooks";
import { EventComponent as EventComponent_ } from "../interfaces/base";
import styled from "@emotion/styled";
import RenderChildren from "../RenderChildren";
import usePageLanguage from "../hooks/usePageLanguage";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useShoppingCart } from "../context/ShoppingCartContext";
// Remove this import as it causes the build error
// import { fetchApiGet } from "../../../api/api";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Spinner as BootstrapSpinner } from "react-bootstrap";
import "./EventComponent.css";
import {
  getComponentFromStructureMap,
  matchesEditorTarget,
} from "../utilities/componentIdentity";
import { resolveEventProductUuid } from "../utilities/eventCart";
import { getEffectivePrice } from "../utilities/productCatalog";
import { resolveAbsoluteAssetUrl } from "../utilities/helpers";
import {
  clampQuantityToMinAndCap,
  hasMinOrderQtyConstraint,
  isEffectivelySoldOut,
  parseMaxOrderQty,
  resolveDisplayAvailable,
  resolveEffectiveMinQty,
  resolvePickerDisplayAvailable,
  resolveStartQuantity,
} from "../utilities/quantityCaps";

// Create an inline version of the API functions we need
// This replicates the functionality from api.ts
declare var defaultApiConfig: any;
declare var API: any;

// Create inline functions that replicate the api.ts functionality
function fetchApi<TResponse>(
  url: string,
  config: RequestInit = {},
  jsonType: boolean = true
): Promise<TResponse> {
  const newConfig = {
    ...(typeof defaultApiConfig !== "undefined" ? defaultApiConfig : {}),
    ...config,
    headers: {
      ...(typeof defaultApiConfig !== "undefined" && defaultApiConfig.headers
        ? defaultApiConfig.headers
        : {}),
      ...(config.headers || {}),
    },
  };

  // Use API.url if available, otherwise empty string
  const apiUrl = typeof API !== "undefined" && API.url ? API.url : "";

  if (!jsonType) {
    return fetch(`${apiUrl}${url}`, newConfig).then(
      (data) => data as unknown as TResponse
    );
  }

  return fetch(`${apiUrl}${url}`, newConfig)
    .then((response) => response.json())
    .then((data) => data as TResponse);
}

// Replicate the getHeaders function
function getHeaders(params: {} = {}) {
  return {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      // Include authorization if it exists in defaultApiConfig
      ...(typeof defaultApiConfig !== "undefined" &&
      defaultApiConfig.headers &&
      defaultApiConfig.headers.Authorization
        ? { Authorization: defaultApiConfig.headers.Authorization }
        : {}),
    },
    params: params,
  };
}

// Inline implementation of fetchApiGet
function fetchApiGet<TResponse>(
  url: string,
  params: {} = {}
): Promise<TResponse> {
  return fetchApi(url, getHeaders(params));
}

// Simple cache implementation for axios calls (only for non-builder environment)
interface CacheEntry {
  data: any;
  timestamp: number;
  expiresIn: number; // milliseconds
}

// Cache storage - using a Map for simplicity
const axiosCache = new Map<string, CacheEntry>();

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Cached axios wrapper function
async function cachedAxiosGet(url: string): Promise<any> {
  // Create cache key from the full URL
  const cacheKey = url;

  // Check if we have a valid cached entry
  const cached = axiosCache.get(cacheKey);
  const now = Date.now();

  if (cached && now < cached.timestamp + cached.expiresIn) {
    return cached.data;
  }

  // Make the actual API call
  // @ts-ignore
  const response = await window.axios.get(url);

  // Store in cache
  axiosCache.set(cacheKey, {
    data: response,
    timestamp: now,
    expiresIn: CACHE_DURATION,
  });

  return response;
}

// Create a simple spinner component directly instead of importing from BuilderComponents
const SimpleSpinner = (): JSX.Element => {
  return <BootstrapSpinner animation="border" role="status" as="div" />;
};

const Div = styled("div")(
  null,
  (props) =>
    function (props: any) {
      return { ...props["data-style"] };
    }
);


function parseMoneyValue(value: any): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const normalized = value
      .trim()
      .replace(/\s/g, "")
      .replace(",", ".")
      .replace(/[^0-9.-]/g, "");
    if (!normalized) {
      return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function firstMoneyValue(...values: any[]): number | null {
  for (const value of values) {
    const parsed = parseMoneyValue(value);
    if (parsed !== null) {
      return parsed;
    }
  }
  return null;
}

// Helper function to format price
function formatPrice(price) {
  return (parseMoneyValue(price) ?? 0).toFixed(2);
}

// Helper function to format date
function formatDate(dateString) {
  const date = new Date(dateString);

  // Add 3 hours to the date
  date.setHours(date.getHours());
  return date.toLocaleString("et-EE", {
    hour: "2-digit",
    minute: "2-digit",
  });
}


// Helper function to format date for the new API (simple YYYY-MM-DD format)
function formatDateForNewApi(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getTicketEffectiveMax(product: any): number {
  return resolveDisplayAvailable(product?.maxAvailable, product?.maxOrderQty);
}

function getTicketmaxOrderQty(product: any): number | undefined {
  return parseMaxOrderQty(product?.maxOrderQty);
}

function getTicketEffectiveMin(
  ticketProduct: any,
  enforceFirstOrderMin: boolean
): number {
  return resolveEffectiveMinQty(
    ticketProduct?.minQty,
    ticketProduct?.minOrderQty,
    enforceFirstOrderMin
  );
}

function parseDisabledDayKey(value: any): string | null {
  const raw = String(value ?? "").trim();
  if (!raw) return null;

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;

  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

type EventDatesPayload = {
  disabledEvents?: unknown[];
  firstAvailableDate?: { forAll?: string | null; forGroup?: Record<string, string> } | null;
};

function extractEventDatesPayload(raw: any): EventDatesPayload | null {
  const isPayload = (value: any): value is EventDatesPayload =>
    Boolean(
      value &&
        typeof value === "object" &&
        ("disabledEvents" in value || "firstAvailableDate" in value)
    );

  if (isPayload(raw)) return raw;
  if (isPayload(raw?.data)) return raw.data;
  if (isPayload(raw?.data?.data)) return raw.data.data;
  return null;
}

const languageTranslations = {
  et: {
    loading_events: "Laen sündmusi...",
    no_events_available: "Sellel kuupäeval ei ole sündmusi",
    loading_events_information: "Laen sündmust...",
    error_loading_events: "Vabade kohtadega sündmuseid ei leitud",
    ticket_type: "Tüüp",
    price: "Hind",
    quantity: "Kogus",
    total: "Summa",
    add_to_cart: "LISA OSTUKORVI",
  },
  en: {
    loading_events: "Loading events...",
    no_events_available: "No events available on this date",
    loading_events_information: "Loading event information...",
    error_loading_events: "No available events found",

    ticket_type: "Ticket Type",
    price: "Price",
    quantity: "Quantity",
    total: "Sum",
    add_to_cart: "ADD TO CART",
  },
};

export default function EventComponent({
  id,
  uuid,
  attributes,
  childElements,
  childWebpageComponentRelations,
}: EventComponent_) {
  const editorId = useAppSelector((state) => state.textEditor?.get("id"));
  const component = (useAppSelector((state) =>
    getComponentFromStructureMap(state.builder.structure.components, uuid ?? id)
  ) as EventComponent_) ?? ({
    id: typeof id === "number" ? id : -1,
    parent_id: null,
    childElements: childElements ?? [],
    childWebpageComponentRelations: childWebpageComponentRelations ?? [],
    sort_order: 0,
    type: "eventComponent",
    uuid: uuid ?? null,
    version: 1,
    webpage_id: 0,
    attributes,
  } as EventComponent_);

  const isBuilder =
    typeof document !== "undefined" &&
    !!document.getElementById("BuilderContent");

  const {
    increaseCartQuantity,
    products,
    getCatalogProductByUuid,
    getCatalogEntryByUuid,
    isFirstOrderMinQtyEnforced,
    registerEventSellOrderFlags,
    order,
  } = useShoppingCart();

  const today = new Date();
  const [selectedDate, setSelectedDate] = useState<Date>(today);
  const [dateEvents, setDateEvents] = useState<any[]>([]);
  const [loadingDateEvents, setLoadingDateEvents] = useState<boolean>(false);

  // Add state for disabled dates
  const [disabledDates, setDisabledDates] = useState<Date[]>([]);
  const [loadingDisabledDates, setLoadingDisabledDates] =
    useState<boolean>(false);

  const navigate = useNavigate();
  const param_id = useParams().id;
  const locale = usePageLanguage();

  // State for event and product data
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [groupEvents, setGroupEvents] = useState<any[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isEventGroup, setIsEventGroup] = useState<boolean>(false);

  // State for ticket quantities
  const [ticketQuantities, setTicketQuantities] = useState<
    Record<string, number>
  >({});

  const isTicketAddToCartDisabled = Object.values(ticketQuantities).every(
    (q) => q <= 0
  );

  // State for keeping typography styles
  const [typographyStyles, setTypographyStyles] = useState<React.CSSProperties>(
    {}
  );

  // State for tooltip
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const [tooltipEventHash, setTooltipEventHash] = useState<string | null>(
    null
  );
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });
  const [tooltipPlacement, setTooltipPlacement] = useState<"top" | "bottom">(
    "top"
  );

  const typographies = useAppSelector((state) => state.builder.typographies);
  const colors = useAppSelector((state) => state.builder.colors);
  const website = useAppSelector((state) => state.builder.website);

  // Get typography styles
  useEffect(() => {
    // Find the default typography
    const defaultTypography = website.defaultTypographyId
      ? typographies.find((x) => x.id === website.defaultTypographyId)
      : typographies.find((x) => x.tag === "p");

    if (defaultTypography) {
      setTypographyStyles({});
    }
  }, [website, typographies, colors]);

  // Fetch event and product data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      const eventHash = component.attributes?.event_hash;
      const eventGroupHash = component.attributes?.event_group_hash;

      if (!eventHash && !eventGroupHash) {
        setError("No event or event group selected");
        setLoading(false);
        setIsEventGroup(false);
        return;
      }

      try {
        if (eventHash) {
          setIsEventGroup(false);
          await fetchSingleEvent(eventHash);
        } else if (eventGroupHash) {
          setIsEventGroup(true);
          await fetchEventsByGroup(eventGroupHash);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching event data:", err);
        setError("Failed to load event data");
        setLoading(false);
      }
    };

    fetchData();
  }, [
    component.attributes?.event_hash,
    component.attributes?.event_group_hash,
    products,
  ]);

  // Fetch a single event by hash or ID (backend accepts both in /event/{idOrHash})
  const fetchSingleEvent = async (eventRef: string) => {
    try {
      let eventData;

      if (isBuilder) {
        const eventResponse = await fetchApiGet(`/event/${eventRef}`);
        eventData = eventResponse;

        if (eventData) {
          registerEventSellOrderFlags([eventData]);
          setSelectedEvent(eventData);
        } else {
          setError("Sündmust ei leitud");
        }
      } else {
        // @ts-ignore
        const eventsResponse = await cachedAxiosGet(`event/${eventRef}`);

        if (eventsResponse.data) {
          registerEventSellOrderFlags([eventsResponse.data]);
          setSelectedEvent(eventsResponse.data);
        }
      }
    } catch (err) {
      console.error("Error fetching single event:", err);
      throw err;
    }
  };

  // Fetch events by group hash
  const fetchEventsByGroup = async (groupHash: string) => {
    try {
      let eventsData;

      if (isBuilder) {
        // In builder, use fetchApiGet with builderApi routes
        const eventsResponse = await fetchApiGet<any>(
          `/events?groupHash=${groupHash}`
        );

        // The API returns an object with a 'data' array
        if (eventsResponse.data) {
          eventsData = eventsResponse.data;
        }

        if (eventsData && eventsData.length > 0) {
          registerEventSellOrderFlags(eventsData);
          setGroupEvents(eventsData);
        } else {
          setError("Sellel grupil puuduvad sündmused");
        }
      } else {
        // @ts-ignore
        const axiosResponse = await cachedAxiosGet(
          `events?groupHash=${groupHash}`
        );

        // Axios throws for non-2xx HTTP statuses by default.
        // If we are here, the request was successful (status 2xx).
        // The user's provided JSON structure for lists is { data: { data: [...] } }
        if (axiosResponse.data && axiosResponse.data.data) {
          eventsData = axiosResponse.data.data;
        } else {
          console.warn(
            "Events by group response (non-builder) did not contain events in data.data:",
            axiosResponse.data
          );
          eventsData = []; // Default to empty array if data is not as expected
        }

        if (eventsData && eventsData.length > 0) {
          registerEventSellOrderFlags(eventsData);
          setGroupEvents(eventsData);
          // Don't set selectedEvent here - fetchEventsByDate will handle it
          // after fetchDisabledEvents determines the correct date to display
        } else {
          setError("Sellel grupil puuduvad sündmused");
        }
      }
    } catch (err) {
      console.error("Error fetching events by group:", err);
      throw err;
    }
  };

  // Fetch product data for an event

  // Select a different event from the group
  const handleSelectEvent = (event: any) => {
    setSelectedEvent(event);

    setTicketQuantities({});
  };

  function getEventTicketProducts(event: any): any[] {
    return event?.ticketProducts || [];
  }

  const resolveTicketProductUuid = useCallback(
    (ticketProduct: any): string | undefined =>
      resolveEventProductUuid(ticketProduct),
    []
  );

  const getTicketIndexKey = (ticketIndex: number): string => String(ticketIndex);

  const getTicketUnitPrice = useCallback(
    (ticketProduct: any): number => {
      const ticketPrice = firstMoneyValue(
        ticketProduct?.price,
        ticketProduct?.totalPrice,
        ticketProduct?.priceTotal,
        ticketProduct?.price_total,
        ticketProduct?.unitPrice,
        ticketProduct?.unit_price,
        ticketProduct?.priceRegular,
        ticketProduct?.price_regular,
        ticketProduct?.product?.price,
        ticketProduct?.product?.totalPrice,
        ticketProduct?.product?.priceRegular
      );
      if (ticketPrice !== null) {
        return ticketPrice;
      }

      const productUuid = resolveTicketProductUuid(ticketProduct);
      if (!productUuid) {
        return 0;
      }

      const entry = getCatalogEntryByUuid(productUuid);
      if (!entry) {
        return 0;
      }

      const { price } = getEffectivePrice(entry, new Date());
      return firstMoneyValue(price, entry.variant?.price, entry.parent?.price) ?? 0;
    },
    [getCatalogEntryByUuid, resolveTicketProductUuid]
  );

  const ticketSelectionTotal = useMemo(() => {
    const ticketProducts = selectedEvent?.ticketProducts;
    if (!Array.isArray(ticketProducts) || ticketProducts.length === 0) {
      return 0;
    }

    return ticketProducts.reduce((sum, ticketProduct, ticketIndex) => {
      const quantity = ticketQuantities[getTicketIndexKey(ticketIndex)] ?? 0;
      if (quantity <= 0) {
        return sum;
      }
      return sum + getTicketUnitPrice(ticketProduct) * quantity;
    }, 0);
  }, [selectedEvent?.ticketProducts, ticketQuantities, getTicketUnitPrice]);

  const getTicketImage = useCallback(
    (ticketProduct: any): string | undefined => {
      const productUuid = resolveTicketProductUuid(ticketProduct);
      if (!productUuid) {
        return undefined;
      }
      const entry = getCatalogEntryByUuid(productUuid);
      const image = entry?.parent.firstFileUrl;
      const resolvedImage = resolveAbsoluteAssetUrl(image);
      if (resolvedImage) {
        return resolvedImage;
      }
      return undefined;
    },
    [getCatalogEntryByUuid, resolveTicketProductUuid]
  );

  // Add ticket to cart with quantity
  const handleAddToCart = () => {
    if (!selectedEvent) return;
    const ticketProducts = getEventTicketProducts(selectedEvent);
    if (ticketProducts.length === 0) return;
    const eventRef = selectedEvent.hash;
    if (eventRef == null) return;

    // Filter tickets with quantity > 0
    const ticketsToAdd = ticketProducts
      .map((ticketProduct, ticketIndex) => {
        const effectiveMax = getTicketEffectiveMax(ticketProduct);
        const effectiveMin = getTicketEffectiveMin(
          ticketProduct,
          isFirstOrderMinQtyEnforced(eventRef)
        );
        const minForClamp = effectiveMin > 1 ? effectiveMin : undefined;
        const rawQty = ticketQuantities[getTicketIndexKey(ticketIndex)] || 0;
        const quantity = clampQuantityToMinAndCap(
          rawQty,
          minForClamp,
          effectiveMax
        );
        return {
          productUuid: resolveTicketProductUuid(ticketProduct) ?? "",
          sortOrder: ticketProduct?.sortOrder || 0,
          maxAvailable: ticketProduct?.maxAvailable,
          maxOrderQty: getTicketmaxOrderQty(ticketProduct),
          minOrderQty: ticketProduct?.minOrderQty,
          minQty: ticketProduct?.minQty,
          salesPeriodEndBeforeEventDatetime:
            ticketProduct?.salesPeriodEndBeforeEventDatetime,
          quantity,
        };
      })
      .filter(({ quantity, productUuid }) => quantity > 0 && productUuid.length > 0)
      .map(({
        productUuid,
        sortOrder,
        maxAvailable,
        maxOrderQty,
        minOrderQty,
        minQty,
        salesPeriodEndBeforeEventDatetime,
        quantity,
      }) => ({
        productUuid,
        sortOrder,
        maxAvailable,
        maxOrderQty,
        minOrderQty,
        minQty,
        salesPeriodEndBeforeEventDatetime,
        quantity,
      }));

    if (ticketsToAdd.length === 0) return;

    // Sort tickets by sortOrder to maintain correct order when adding simultaneously
    const sortedTicketsToAdd = ticketsToAdd.sort(
      (a, b) => a.sortOrder - b.sortOrder
    );

    // Add each ticket to cart in sorted order
    sortedTicketsToAdd.forEach(
      ({
        productUuid,
        maxAvailable,
        maxOrderQty,
        minOrderQty,
        minQty,
        salesPeriodEndBeforeEventDatetime,
        quantity,
      }) => {
        const selectedDate = new Date(selectedEvent.start).toISOString();
        // Add to cart the specified quantity (one at a time)
        for (let i = 0; i < quantity; i++) {
          increaseCartQuantity(
            productUuid,
            eventRef,
            selectedDate,
            true,
            salesPeriodEndBeforeEventDatetime,
            maxAvailable,
            maxOrderQty,
            minOrderQty,
            minQty,
            selectedEvent.hasActiveSellOrders,
          );
        }
      }
    );

    // Clear the ticket quantities after all items are added
    setTicketQuantities({});

    // Redirect if a redirect URL is configured
    const redirectUrl = component.attributes?.add_to_cart_redirect_url;
    if (redirectUrl) {
      navigate(redirectUrl);
    }
  };

  // Update ticket quantity
  const handleQuantityChange = (
    ticketIndex: number,
    quantity: number,
    effectiveMax: number,
    minQty?: number
  ) => {
    if (
      quantity > 0 &&
      isEffectivelySoldOut(minQty, effectiveMax)
    ) {
      return;
    }
    const quantityKey = getTicketIndexKey(ticketIndex);
    const qty =
      quantity <= 0
        ? 0
        : clampQuantityToMinAndCap(quantity, minQty, effectiveMax);

    setTicketQuantities((prev) => ({
      ...prev,
      [quantityKey]: qty,
    }));
  };

  // Fetch events for a specific date
  const fetchEventsByDate = async (date: Date) => {
    setLoadingDateEvents(true);

    try {
      // Get event group hash from component attributes
      const eventGroupHash = component.attributes?.event_group_hash;

      if (!eventGroupHash) {
        console.error("No event group hash found");
        setError("Grupi hash'i ei leitud");
        setLoadingDateEvents(false);
        return;
      }

      // Format date for the new API (simple YYYY-MM-DD format)
      const dateFormatted = formatDateForNewApi(date);

      let eventsResponse;
      let events: any[] = [];

      if (isBuilder) {
        // Make API call with builderApi routes
        eventsResponse = await fetchApiGet<any>(
          `/events?date=${encodeURIComponent(
            dateFormatted
          )}&groupHash=${eventGroupHash}&perPage=100&sortBy=start&direction=asc`
        );

        // Handle different response formats
        if (Array.isArray(eventsResponse)) {
          events = eventsResponse;
        } else if (
          eventsResponse &&
          typeof eventsResponse === "object" &&
          "data" in eventsResponse &&
          Array.isArray(eventsResponse.data)
        ) {
          events = eventsResponse.data;
        }
      } else {
        // @ts-ignore
        const axiosResponse = await cachedAxiosGet(
          `events?date=${encodeURIComponent(
            dateFormatted
          )}&groupHash=${eventGroupHash}&perPage=100&sortBy=start&direction=asc`
        );

        // Axios throws for non-2xx HTTP statuses by default.
        // If we are here, the request was successful (status 2xx).
        // The user's provided JSON structure for lists is { data: { data: [...] } }
        if (axiosResponse.data && axiosResponse.data.data) {
          events = axiosResponse.data.data;
        } else {
          // It's possible that a 2xx response might not have the expected data structure.
          console.warn(
            "Events by date response (non-builder) did not contain events in data.data:",
            axiosResponse.data
          );
          events = []; // Default to empty array if data is not as expected
        }
      }

      // Update state with fetched events
      if (events.length > 0) {
        registerEventSellOrderFlags(events);
        setDateEvents(events);
        setGroupEvents(events);

        // Select the first event by default if we don't have one selected yet
        if (
          !selectedEvent ||
          events.findIndex((e) => e.hash === selectedEvent.hash) === -1
        ) {
          const firstEvent = events[0];
          setSelectedEvent(firstEvent);
        }
      } else {
        setDateEvents([]);
        setGroupEvents([]);
        setSelectedEvent(null);

        setTicketQuantities({});
      }

      setLoadingDateEvents(false);
    } catch (err) {
      console.error("Error fetching events by date:", err);
      setDateEvents([]);
      setLoadingDateEvents(false);
    }
  };

  // Handle calendar date change
  const handleCalendarChange = (value: any) => {
    if (value instanceof Date) {
      setSelectedDate(value);
      fetchEventsByDate(value);
    }
  };

  // Tooltip handlers
  const handleMouseEnter = (event: any, eventData: any) => {
    // Don't show tooltip on mobile devices
    if (window.innerWidth < 658) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 300; // max width of tooltip
    const tooltipHeight = 200; // estimated height
    const buffer = 10; // buffer space from screen edges

    // Calculate available space in all directions
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;

    let x, y;
    let preferredPosition: "top" | "bottom" = "top"; // default preference

    // Determine the best vertical position
    if (spaceAbove >= tooltipHeight + buffer) {
      // Enough space above - position tooltip above the button
      preferredPosition = "top";
      y = rect.top - buffer; // Position just above the button
    } else if (spaceBelow >= tooltipHeight + buffer) {
      // Not enough space above, but enough below - position below
      preferredPosition = "bottom";
      y = rect.bottom + buffer;
    } else {
      // Not enough space above or below - choose the side with more space
      if (spaceAbove > spaceBelow) {
        preferredPosition = "top";
        y = buffer; // Position at top of screen with buffer
      } else {
        preferredPosition = "bottom";
        y = window.innerHeight - tooltipHeight - buffer; // Position at bottom with buffer
      }
    }

    // Calculate horizontal position (always center on the button if possible)
    x = rect.left + rect.width / 2;

    // Adjust horizontal position if tooltip would go off screen
    const halfTooltipWidth = tooltipWidth / 2;

    if (x - halfTooltipWidth < buffer) {
      // Would go off left edge
      x = halfTooltipWidth + buffer;
    } else if (x + halfTooltipWidth > window.innerWidth - buffer) {
      // Would go off right edge
      x = window.innerWidth - halfTooltipWidth - buffer;
    }

    setTooltipPosition({ x, y });
    setTooltipPlacement(preferredPosition);
    setTooltipEventHash(eventData.hash);
    setTooltipVisible(true);
  };

  const handleMouseLeave = () => {
    // Hide tooltip immediately when leaving the button
    setTooltipVisible(false);
    setTooltipEventHash(null);
  };

  const handleTooltipMouseEnter = () => {
    // Keep tooltip visible when hovering over it
    setTooltipVisible(true);
  };

  const handleTooltipMouseLeave = () => {
    // Hide tooltip immediately when leaving the tooltip itself
    setTooltipVisible(false);
    setTooltipEventHash(null);
  };

  // Add function to fetch disabled events
  const fetchDisabledEvents = async (startDate: Date, endDate: Date) => {
    setLoadingDisabledDates(true);

    try {
      // Get event group hash from component attributes
      const eventGroupHash = component.attributes?.event_group_hash;

      if (!eventGroupHash) {
        console.error("No event group hash found for disabled events");
        setLoadingDisabledDates(false);
        return;
      }

      // Format dates for API (YYYY.MM.DD format)
      const formatDateForDisabledApi = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}.${month}.${day}`;
      };

      const startFormatted = formatDateForDisabledApi(startDate);
      const endFormatted = formatDateForDisabledApi(endDate);

      let payload: EventDatesPayload | null = null;

      if (isBuilder) {
        const resp = await fetchApiGet<any>(
          `/event-dates?start=${startFormatted}&end=${endFormatted}&groupHash=${eventGroupHash}`
        );
        payload = extractEventDatesPayload(resp);
      } else {
        // @ts-ignore
        const resp = await cachedAxiosGet(
          `event-dates?start=${startFormatted}&end=${endFormatted}&groupHash=${eventGroupHash}`
        );
        payload = extractEventDatesPayload(resp);
      }

      const disabledEvents =
        payload != null && Array.isArray(payload.disabledEvents)
          ? payload.disabledEvents
          : [];
      const disabledDayKeys = disabledEvents
        .map((ds) => parseDisabledDayKey(ds))
        .filter((k): k is string => Boolean(k));
      const disabledDatesArray = disabledDayKeys.map((key) => {
        const [year, month, day] = key.split("-").map(Number);
        return new Date(year, month - 1, day);
      });

      setDisabledDates(disabledDatesArray);

      // Check if today is disabled and find first available date
      const todayMidnight = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );
      const isTodayDisabled = disabledDatesArray.some(
        (disabledDate) => disabledDate.getTime() === todayMidnight.getTime()
      );

      if (isTodayDisabled) {
        // If today is disabled, find the first available date
        let firstAvailableDate: Date | null = null;

        // Start from today and look forward for the first available date
        const currentDate = new Date(todayMidnight);
        const maxDate = new Date(endDate); // Don't go beyond our fetched range

        while (currentDate <= maxDate) {
          const isCurrentDateDisabled = disabledDatesArray.some(
            (disabledDate) => disabledDate.getTime() === currentDate.getTime()
          );

          if (!isCurrentDateDisabled) {
            firstAvailableDate = new Date(currentDate.getTime());
            break;
          }

          // Move to next day
          currentDate.setDate(currentDate.getDate() + 1);
        }

        // If we found an available date, set it as selected and fetch events for it
        if (firstAvailableDate) {
          setSelectedDate(firstAvailableDate);
          // Fetch events for the new selected date
          fetchEventsByDate(firstAvailableDate);
        }
      } else {
        // Today is not disabled, so fetch events for today
        fetchEventsByDate(today);
      }

      setLoadingDisabledDates(false);
    } catch (err) {
      console.error("Error fetching disabled events:", err);
      setDisabledDates([]);
      setLoadingDisabledDates(false);
    }
  };

  // Add function to handle calendar navigation (month changes)
  const handleActiveStartDateChange = ({
    activeStartDate,
  }: {
    activeStartDate: Date | null;
  }) => {
    if (!activeStartDate || !isEventGroup) return;

    // Get the first day of the month being viewed
    const startOfMonth = new Date(
      activeStartDate.getFullYear(),
      activeStartDate.getMonth(),
      1
    );

    // Get the date 6 months after the start of the month
    const endOfSixMonths = new Date(startOfMonth);
    endOfSixMonths.setMonth(endOfSixMonths.getMonth() + 6);
    endOfSixMonths.setDate(0); // Set to last day of the month 6 months ahead

    // Add 7 day buffer to startOfMonth to account for calendar showing days from previous month
    const startOfMonthWithBuffer = new Date(startOfMonth);
    startOfMonthWithBuffer.setDate(startOfMonth.getDate() - 7);

    // Add 1 week buffer to endOfSixMonths to account for calendar showing days from next month
    const endOfSixMonthsWithBuffer = new Date(endOfSixMonths);
    endOfSixMonthsWithBuffer.setDate(endOfSixMonths.getDate() + 7);

    // Fetch disabled dates for the next 6 months
    fetchDisabledEvents(startOfMonthWithBuffer, endOfSixMonthsWithBuffer);
  };

  // Add function to check if a date should be disabled
  const isDateDisabled = ({ date }: { date: Date }) => {
    // Create a date object for comparison (set to midnight)
    const dateToCheck = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    // Check if this date is in the disabled dates array
    return disabledDates.some(
      (disabledDate) => disabledDate.getTime() === dateToCheck.getTime()
    );
  };

  // Initial fetch for disabled events when component mounts and it's an event group
  useEffect(() => {
    if (isEventGroup) {
      // Fetch disabled events for the next 6 months starting from today
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

      // Get the date 6 months after the start of the current month
      const endOfSixMonths = new Date(startOfMonth);
      endOfSixMonths.setMonth(endOfSixMonths.getMonth() + 6);
      endOfSixMonths.setDate(0); // Set to last day of the month 6 months ahead

      // Add 7 day buffer to startOfMonth to account for calendar showing days from previous month
      const startOfMonthWithBuffer = new Date(startOfMonth);
      startOfMonthWithBuffer.setDate(startOfMonth.getDate() - 7);

      // Add 1 week buffer to endOfSixMonths to account for calendar showing days from next month
      const endOfSixMonthsWithBuffer = new Date(endOfSixMonths);
      endOfSixMonthsWithBuffer.setDate(endOfSixMonths.getDate() + 7);

      // Fetch disabled events (this will also handle setting the correct selected date and fetching events for it)
      fetchDisabledEvents(startOfMonthWithBuffer, endOfSixMonthsWithBuffer);
    }
  }, [isEventGroup]);

  // Handle scroll events to hide tooltip
  useEffect(() => {
    const handleScroll = () => {
      if (tooltipVisible) {
        setTooltipVisible(false);
        setTooltipEventHash(null);
      }
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll, true); // Use capture phase to catch all scroll events

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [tooltipVisible]);

  // Show loading or error state
  if (loading) {
    return (
      <Div
        id={id.toString()}
        key={id}
        data-style={attributes?.styles || {}}
        className={
          attributes &&
          "event-component " +
            attributes.classes +
            (isBuilder ||
            (attributes.classes != "container" &&
              attributes.classes != "container-fluid")
              ? " builder-component builder-component__event_component"
              : "") +
            (matchesEditorTarget({ id, uuid }, editorId) ? " builder-component--active" : "")
        }
        data-component-type="section"
      >
        <div
          style={{
            fontSize: "15px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
          }}
        >
          <SimpleSpinner />
          <p style={{ color: "#718096", margin: 0 }}>
            {languageTranslations[locale]?.loading_events_information}
          </p>
        </div>
      </Div>
    );
  }

  if (error && !selectedEvent) {
    return (
      <Div
        id={id.toString()}
        key={id}
        data-style={attributes?.styles || {}}
        className={
          attributes &&
          "event-component " +
            attributes.classes +
            (isBuilder ||
            (attributes.classes != "container" &&
              attributes.classes != "container-fluid")
              ? " builder-component builder-component__event_component"
              : "") +
            (matchesEditorTarget({ id, uuid }, editorId) ? " builder-component--active" : "")
        }
        data-component-type="section"
      >
        <RenderChildren
          key={id + "children"}
          childWebpageComponentRelations={childWebpageComponentRelations}
        />
        <p
          style={{
            ...typographyStyles,
            fontSize: "15px",
            textAlign: "center",
            fontWeight: 500,
            color: "#212529",
          }}
        >
          {languageTranslations[locale]?.error_loading_events}
        </p>
      </Div>
    );
  }

  return (
    <Div
      id={id.toString()}
      key={id}
      data-style={attributes?.styles || {}}
      className={
        attributes &&
        "event-component " +
          attributes.classes +
          (isBuilder ||
          (attributes.classes != "container" &&
            attributes.classes != "container-fluid")
            ? " builder-component builder-component__event_component"
            : "") +
          (matchesEditorTarget({ id, uuid }, editorId) ? " builder-component--active" : "")
      }
      data-component-type="section"
      style={{ height: "100%" }}
    >
      <RenderChildren
        key={id + "children"}
        childWebpageComponentRelations={childWebpageComponentRelations}
      />

      <div style={{ color: "#212529", fontFamily: "Roboto", fontSize: "15px" }}>
        {isEventGroup && (
          <div style={{ marginBottom: "2rem" }}>
            <div className="event-component-table-container-mobile">
              <Calendar
                className="event-calendar"
                minDate={new Date()}
                next2Label={null}
                prev2Label={null}
                onChange={handleCalendarChange}
                onActiveStartDateChange={handleActiveStartDateChange}
                value={selectedDate}
                locale="et-EE"
                tileDisabled={isDateDisabled}
                formatShortWeekday={(locale, date) => {
                  const days = ["P", "E", "T", "K", "N", "R", "L"]; // Estonian weekday abbreviations
                  return days[date.getDay()];
                }}
                formatMonth={(locale, date) => {
                  const months = [
                    "Jaanuar",
                    "Veebruar",
                    "Märts",
                    "Aprill",
                    "Mai",
                    "Juuni",
                    "Juuli",
                    "August",
                    "September",
                    "Oktoober",
                    "November",
                    "Detsember",
                  ];
                  return months[date.getMonth()];
                }}
              />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <h4
                  style={{
                    fontSize: "15px",
                    fontWeight: 600,
                    marginBottom: "20px",
                    color: "#212529",
                  }}
                >
                  {selectedDate
                    .toLocaleDateString("et-EE", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })
                    .replace(/^[a-z]/, (c) => c.toUpperCase())}
                </h4>
                {loadingDateEvents ? (
                  <p
                    style={{
                      textAlign: "left",
                      fontWeight: 500,
                      color: "#212529",
                    }}
                  >
                    {languageTranslations[locale]?.loading_events}
                  </p>
                ) : dateEvents.length > 0 ? (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "0.8rem",
                    }}
                  >
                    {dateEvents.map((event) => (
                      <button
                        key={event.hash ?? `${event.start}-${event.end}`}
                        onClick={() => handleSelectEvent(event)}
                        className={
                          "shop-controlled-border-radius " +
                          (!event.eventCategory ||
                          !event.eventCategory.displayColor
                            ? selectedEvent?.hash === event.hash
                              ? "event-component-button-selected"
                              : "event-component-button"
                            : "")
                        }
                        style={{
                          fontWeight: "600",
                          fontSize: "14px",
                          padding: "5px 15px",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          ...(event.eventCategory &&
                          event.eventCategory.displayColor
                            ? {
                                border: `1px solid ${event.eventCategory.displayColor}`,
                                backgroundColor:
                                  selectedEvent?.hash === event.hash
                                    ? event.eventCategory.displayColor
                                    : "transparent",
                                color:
                                  selectedEvent?.hash === event.hash
                                    ? "#fff"
                                    : event.eventCategory.displayColor,
                              }
                            : {}),
                        }}
                        onMouseEnter={(e) => handleMouseEnter(e, event)}
                        onMouseLeave={handleMouseLeave}
                      >
                        <span>{formatDate(event.start)}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p
                    style={{
                      textAlign: "left",
                      fontWeight: 500,
                      color: "#212529",
                      ...typographyStyles,
                    }}
                  >
                    {languageTranslations[locale]?.no_events_available}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Only show event details if we have a selected event */}
        {selectedEvent && (
          <>
            {/* Ticket Selection */}
            {selectedEvent &&
            selectedEvent.ticketProducts &&
            selectedEvent.ticketProducts.length > 0 ? (
              <div style={{ marginTop: "2rem" }}>
                <div style={{ marginTop: "1.5rem" }}>
                  <div className="event-component-table-container">
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "600",
                        color: "#212529",
                        textAlign: "left",
                      }}
                    >
                      {languageTranslations[locale]?.ticket_type}
                    </div>
                    <div
                      style={{
                        textAlign: "center",
                        color: "#212529",
                        fontSize: "15px",
                        fontWeight: "600",
                      }}
                    >
                      {languageTranslations[locale]?.quantity}
                    </div>
                    <div
                      style={{
                        textAlign: "right",
                        fontSize: "15px",
                        fontWeight: "600",
                        color: "#212529",
                      }}
                    >
                      {languageTranslations[locale]?.price}
                    </div>
                  </div>

                  {selectedEvent.ticketProducts.map(
                    (product, ticketIndex) => {
                      const effectiveMax = getTicketEffectiveMax(product);
                      const effectiveMin = getTicketEffectiveMin(
                        product,
                        isFirstOrderMinQtyEnforced(selectedEvent.hash ?? "")
                      );
                      const minForUi = effectiveMin > 1 ? effectiveMin : undefined;
                      const isSoldOut = isEffectivelySoldOut(
                        minForUi,
                        effectiveMax
                      );
                      const pickerDisplayAvailable = resolvePickerDisplayAvailable(
                        minForUi,
                        effectiveMax
                      );
                      const quantityKey = getTicketIndexKey(ticketIndex);
                      const currentQuantity = ticketQuantities[quantityKey] || 0;
                      const unitPrice = getTicketUnitPrice(product);

                      return (
                        <div
                          key={product.uuid ?? product.productId}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 110px 70px",
                            gap: "1.25rem",
                            padding: "0.5rem 0",
                            color: isSoldOut ? "#616161" : "#212529",

                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <div className="cartOrderProductRowImage event-component-image-container">
                              <img
                                src={getTicketImage(product)}
                                alt=""
                                className="cartSidepanelProductImage "
                              />
                            </div>

                            <div
                              style={{
                                fontWeight: 400,
                              }}
                            >
                              {product.productName || product.name}

                              <div
                                style={{
                                  fontSize: "12px",
                                  textAlign: "left",
                                  marginTop: "2px",
                                }}
                              >
                                Saadaval: {pickerDisplayAvailable}
                              </div>
                            </div>
                          </div>

                          {isSoldOut ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <p
                                style={{
                                  fontWeight: 400,
                                  marginBottom: "0px",
                                }}
                              >
                                Välja müüdud!
                              </p>
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                maxWidth: "120px",
                                borderRadius: "8px",
                                border: "1px solid #e2e8f0",
                                overflow: "hidden",
                              }}
                            >
                              <button
                                onClick={() =>
                                  handleQuantityChange(
                                    ticketIndex,
                                    currentQuantity <= effectiveMin
                                      ? 0
                                      : currentQuantity - 1,
                                    effectiveMax,
                                    minForUi
                                  )
                                }
                                disabled={currentQuantity <= 0}
                                style={{
                                  background: "#fff",
                                  border: "none",
                                  width: "36px",
                                  height: "36px",
                                  fontSize: "1.1rem",
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  color:
                                    currentQuantity <= 0
                                      ? "#cbd5e0"
                                      : "#4a5568",
                                }}
                              >
                                -
                              </button>
                              <input
                                type="number"
                                min="0"
                                max={effectiveMax}
                                value={currentQuantity}
                                onChange={(e) =>
                                  handleQuantityChange(
                                    ticketIndex,
                                    parseInt(e.target.value, 10) || 0,
                                    effectiveMax,
                                    minForUi
                                  )
                                }
                                style={{
                                  width: "50px",
                                  height: "36px",
                                  border: "none",
                                  borderLeft: "1px solid #e2e8f0",
                                  borderRight: "1px solid #e2e8f0",
                                  textAlign: "center",
                                  fontSize: "0.9rem",
                                  padding: 0,
                                  MozAppearance: "textfield",
                                  appearance: "textfield",
                                  background: "#fff",
                                }}
                              />
                              <button
                                onClick={() =>
                                  handleQuantityChange(
                                    ticketIndex,
                                    currentQuantity === 0 &&
                                      hasMinOrderQtyConstraint(minForUi)
                                      ? resolveStartQuantity(
                                          minForUi,
                                          effectiveMax
                                        )
                                      : currentQuantity + 1,
                                    effectiveMax,
                                    minForUi
                                  )
                                }
                                disabled={currentQuantity >= effectiveMax}
                                style={{
                                  background: "#fff",
                                  border: "none",
                                  width: "36px",
                                  height: "36px",
                                  fontSize: "1.1rem",
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  color:
                                    currentQuantity >= effectiveMax
                                      ? "#cbd5e0"
                                      : "#4a5568",
                                }}
                              >
                                +
                              </button>
                            </div>
                          )}

                          <div
                            style={{
                              fontWeight: 400,
                              whiteSpace: "nowrap",
                              textAlign: "right",
                            }}
                          >
                            {formatPrice(unitPrice)} €
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>

                {/* Total and Add to Cart */}
                <div
                  style={{
                    marginTop: "1.5rem",
                    paddingTop: "1rem",
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "1rem",
                  }}
                >
                  {/* Only show button if show_add_to_cart_button is not explicitly false */}
                  {component.attributes?.show_add_to_cart_button !== false && (
                    <button
                      type="button"
                      onClick={handleAddToCart}
                      disabled={isTicketAddToCartDisabled}
                      className="btn btn-primary addProductVariableToCart shop-controlled-border-radius"
                      style={{
                        fontFamily: "Roboto",
                        fontSize: "14px",
                        textDecoration: "none",
                        letterSpacing: "0.01em",
                        fontWeight: "600",
                        padding: "5px 15px",
                      }}
                      onMouseEnter={(e) => handleMouseEnter(e, selectedEvent)}
                      onMouseLeave={handleMouseLeave}
                    >
                      {component.attributes?.add_to_cart_button_text ||
                        languageTranslations[locale]?.add_to_cart}
                    </button>
                  )}

                  <div
                    style={{
                      ...typographyStyles,
                      fontSize: "15px",
                      color: "#212529",
                      fontWeight: 600,
                    }}
                  >
                    {languageTranslations[locale]?.total}:{" "}
                    {formatPrice(ticketSelectionTotal)} €
                  </div>
                </div>
              </div>
            ) : (
              // Show message if no product is associated with the event
              <p
                style={{
                  textAlign: "center",
                  fontWeight: 500,
                  color: "#212529",
                  ...typographyStyles,
                }}
              >
                Sellel sündmusel pole ühtegi piletit.
              </p>
            )}
          </>
        )}
      </div>

      {/* Tooltip */}
      {tooltipVisible && tooltipEventHash && (
        <div
          style={{
            position: "fixed",
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform:
              tooltipPlacement === "top"
                ? "translate(-50%, -100%)"
                : "translate(-50%, 0%)",
            backgroundColor: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            zIndex: 1000,
            fontSize: "14px",
            fontFamily: "Roboto",
            color: "#212529",
            minWidth: "200px",
            maxWidth: "300px",
            pointerEvents: "auto",
          }}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        >
          {(() => {
            const hoveredEvent = dateEvents.find(
              (e) => e.hash === tooltipEventHash
            );
            if (
              !hoveredEvent ||
              !hoveredEvent.ticketProducts
            ) {
              return <div>Pileteid pole saadaval.</div>;
            }

            return (
              <div>
                {hoveredEvent.ticketProducts.map(
                  (product) => {
                    const displayAvailable = resolveDisplayAvailable(
                      product?.maxAvailable,
                      product?.maxOrderQty
                    );

                    if (displayAvailable <= 0) {
                      return <></>;
                    }

                    return (
                      <div
                        key={product.uuid ?? product.productId}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "6px 0",
                          gap: "12px",
                          minHeight: "24px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "13px",
                            flex: "1",
                            wordBreak: "break-word",
                            lineHeight: "1.2",
                          }}
                        >
                          {product.productName || product.name}
                        </span>
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: "400",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          Saadaval: {displayAvailable}
                        </span>
                      </div>
                    );
                  }
                )}
              </div>
            );
          })()}
        </div>
      )}
    </Div>
  );
}
